<template>
  <div>
    <div class="tabs mb-3">
      <ul>
        <li 
          @click="setActive('estimation_data')"
          :class="{ 'is-active': active == 'estimation_data' }">
          <a>Estimation Data</a>
        </li>       
        <li 
          v-if="!firstLoad && estimation.rfq.drawing_highlights &&
            estimation.rfq.drawing_highlights.length > 0 && 
            estimation.rfq.drawing_highlights[0].is_approved"
          @click="setActive('drawing_highlights')"
          :class="{ 'is-active': active == 'drawing_highlights' }">
          <a>Drawing Highlights</a>
        </li>
        <li 
          v-if="!firstLoad && hasInsulation"
          @click="setActive('add_insulation')"
          :class="{ 'is-active': active == 'add_insulation' }">
          <a>Add Insulation</a>
        </li> 
        <li 
          @click="setActive('detailed_summary')"
          :class="{ 'is-active': active == 'detailed_summary' }">
          <a>Detailed Summary</a>
        </li>
        <li 
          @click="setActive('summary')"
          :class="{ 'is-active': active == 'summary' }">
          <a>Summary</a>
        </li>
        <li 
          v-if="!firstLoad && details.length > 0"
          @click="setActive('proposal_preview')"
          :class="{ 'is-active': active == 'proposal_preview' }">
          <a>Proposal Preview</a>
        </li>

      </ul>
    </div>

    <div
      v-if="!firstLoad"
      v-show="active == 'estimation_data'" 
      class="estimation__details pb-3">
      <v-fade-transition mode="out-in">
        <div v-if="draftSavedAt" class="mb-2 has-text-grey is-size-7">
          Draft automatically saved at {{ formatDate(draftSavedAt) }}
        </div>
      </v-fade-transition>
      <div :key="estimation.id" id="estimation-data">
      </div>
    </div>

    <div
      v-if="active == 'drawing_highlights'" 
      class="estimation__details pb-3">
      <drawing-highlights 
        :drawing-highlight="estimation.rfq.drawing_highlights[0]"/>
    </div>

    <div
      v-if="active == 'add_insulation'" 
      class="estimation__details pb-3">
      <table class="table is-hoverable is-fullwidth">
        <thead>
          <tr>
            <th
              v-for="(insulationHeader, i) in insulationHeaders"
              :key="i">
              {{ insulationHeader }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            class="v-centered"
            v-for="item in estimation.items.filter(d => d.has_insulation)"
            :key="item.id">
            <!-- <td>
              {{ item.name }}
            </td> -->
            <td>
              {{ item.architecture_name }}
            </td>
            <td>
              <template 
                v-if="item.installations.map(
                  d => d.installation.installation_type_id
                ).includes(3)">
                <template v-for="installation in 
                  item.installations.filter(d => d.installation.installation_type_id == 3)">
                  <v-select 
                    :key="installation.id"
                    :options="installations.filter(d => d.installation_type.id == 3)"
                    item-text="installation_name"
                    item-value="id"
                    :disabled="!editable || blockSaving"
                    v-model="installation.installation_id"
                    @input="updateInsulation(installation, $event)"
                  />
                </template>
              </template>
              <template v-else>
                <v-select 
                  :options="installations.filter(d => d.installation_type.id == 3)"
                  item-text="installation_name"
                  item-value="id"
                  :disabled="!editable || blockSaving"
                  @input="addInsulation(item, $event)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div 
      v-if="active == 'detailed_summary' && !firstLoad"
      class="estimation__details pb-3">

      <article 
        v-if="details.length == 0" 
        class="message is-info is-fullwidth mt-3">
        <div class="message-body">
          There are no details for this estimation.
        </div>
      </article>

      <template v-for="item in estimation.items">
        <table
          :key="item.id" 
          class="table is-fullwidth has-smallest-font">
          <thead>
            <tr>
              <th 
                v-for="(header,i) in detailedSummaryHeaders"
                :colspan="header.colspan"
                :class="header.class"
                :key="i">
                {{ header.label }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr class="v-centered">
              <td
                class="text--nowrap border--after v-top"
                :rowspan="item.materials.length + item.processing.length + 
                item.fabrications.length + item.installations.length + 1">
                {{ item.architecture_name }}
              </td>
            </tr>
            <tr
              class="v-centered" 
              v-for="(material, materialIndex) in item.materials"
              :key="material.id">
              <template v-if="materialIndex == 0" >
                <td
                  class="border--after v-top" 
                  :rowspan="item.materials.length + item.processing.length + 
                    item.fabrications.length">
                  Supply
                </td>
                <td
                  class="border--after v-top"  
                  :rowspan="item.materials.length + item.processing.length">
                  Material
                </td>
              </template>
              <td>
                {{ material.product_master.product_category.category }}
              </td>
              <td class="border--after">
                <div class="flex flex--row flex--align-center">
                  <a 
                    class="flex flex--row flex--align-center mr-2"
                    @click.stop=""
                    :href="material.product_master.image"
                    target="_blank"
                    v-if="material.product_master.image &&
                      material.product_master.product_category_id == 13">
                    <img 
                      width="30px"
                      height="auto"
                      :src="material.product_master.image"/>
                  </a>
                  {{ material.product_master.name }}
                </div>
              </td>
              <td>
                <div class="flex flex--row flex--align-center">
                  <v-text-field
                    v-if="material.manually_adjusted_area == true" 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Area"
                    v-model="material.area"
                    @keyup.enter.native="adjustMaterial(material.id, 
                      { area: material.area, manually_adjusted_area: true })"/>
                  <div v-else>
                    {{ material.area | commasep(0) }}
                  </div>
                  <button
                    :key="material.manually_adjusted_area"
                    v-if="material.manually_adjusted_area == false"
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="material.manually_adjusted_area = true" 
                    class="button is-small ml-2">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                  </button>
                  <template v-else>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="adjustMaterial(material.id, 
                        { area: material.area, manually_adjusted_area: true })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fa fa-save"></i>
                      </span>
                    </button>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="material.manually_adjusted_area = false; 
                        adjustMaterial(material.id, { manually_adjusted_area: false })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fas fa-undo"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
              <td>
                {{ material.raw_area | commasep(0) }}
              </td>
              <td class="border--after">
                {{ material.product_master.unit ? material.product_master.unit.unit_display_name : '' }}
              </td>
              <td>
                {{ material.unit_price | currency(2) }}
              </td>
              <td>
                {{ material.price_bef_adj | currency(1) }}
              </td>
              <td>
                <div class="flex flex--row">
                  <v-text-field 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Unit Price Adjustment"
                    v-model="material.unit_price_adj"
                    @keyup.enter.native="adjustMaterial(
                      material.id, { unit_price_adj: material.unit_price_adj}
                    )"/>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustMaterial(
                      material.id, { unit_price_adj: material.unit_price_adj}
                    )" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </td>
              <td>
                {{ (+material.unit_price + +material.unit_price_adj) | currency(2) }}
              </td>
              <td>
                {{ material.price | currency(1) }}
              </td>
            </tr>

            <tr
              class="v-centered" 
              v-for="itemProcessing in item.processing"
              :key="`processing-${itemProcessing.id}`">
              <td>
                {{ itemProcessing.processing.processing_type.processing_type_name }}
              </td>
              <td class="border--after">
                {{ itemProcessing.processing.processing_name }}
              </td>
              <td>
                <div class="flex flex--row flex--align-center">
                  <v-text-field
                    v-if="itemProcessing.manually_adjusted_area == true" 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Area"
                    v-model="itemProcessing.area"
                    @keyup.enter.native="adjustProcessing(itemProcessing.id, 
                      { area: itemProcessing.area, manually_adjusted_area: true })"/>
                  <div v-else>
                    {{ itemProcessing.area | commasep(0) }}
                  </div>
                  <button
                    :key="itemProcessing.manually_adjusted_area"
                    v-if="itemProcessing.manually_adjusted_area == false"
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="itemProcessing.manually_adjusted_area = true" 
                    class="button is-small ml-2">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                  </button>
                  <template v-else>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="adjustProcessing(itemProcessing.id, 
                        { area: itemProcessing.area, manually_adjusted_area: true })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fa fa-save"></i>
                      </span>
                    </button>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="itemProcessing.manually_adjusted_area = false; 
                        adjustProcessing(itemProcessing.id, { manually_adjusted_area: false })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fas fa-undo"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
              <td>
                {{ itemProcessing.raw_area | commasep(0) }}
              </td>
              <td class="border--after">
                {{ itemProcessing.processing.unit ? itemProcessing.processing.unit.unit_display_name : '' }}
              </td>
              <td>
                {{ itemProcessing.unit_price | currency(2) }}
              </td>
              <td>
                {{ itemProcessing.price_bef_adj | currency(1) }}
              </td>
              <td>
                <div class="flex flex--row">
                  <v-text-field 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Unit Price Adjustment"
                    v-model="itemProcessing.unit_price_adj"
                    @keyup.enter.native="adjustProcessing(itemProcessing.id, 
                      { unit_price_adj: itemProcessing.unit_price_adj })"/>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustProcessing(itemProcessing.id, 
                      { unit_price_adj: itemProcessing.unit_price_adj })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </td>
              <td>
                {{ (+itemProcessing.unit_price + +itemProcessing.unit_price_adj) | currency(2) }}
              </td>
              <td>
                {{ itemProcessing.price | currency(2) }}
              </td>
            </tr>

            <tr 
              class="v-centered"
              v-for="(itemFabrication, fabricationIndex) in item.fabrications"
              :key="`fabrication-${itemFabrication.id}`">
              <td
                class="border--after v-top"
                v-if="fabricationIndex == 0" 
                :rowspan="item.fabrications.length">
                Fabrication
              </td>
              <td>
                {{ itemFabrication.fabrication.fabrication_type.fabrication_type_name }}
              </td>
              <td class="border--after">
                {{ itemFabrication.fabrication.fabrication_name }}
              </td>
              <td>
                <div class="flex flex--row flex--align-center">
                  <v-text-field
                    v-if="itemFabrication.manually_adjusted_area == true" 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Area"
                    v-model="itemFabrication.area"
                    @keyup.enter.native="adjustFabrication(itemFabrication.id, 
                      { area: itemFabrication.area, manually_adjusted_area: true })"/>
                  <div v-else>
                    {{ itemFabrication.area | commasep(0) }}
                  </div>
                  <button
                    :key="itemFabrication.manually_adjusted_area"
                    v-if="itemFabrication.manually_adjusted_area == false"
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="itemFabrication.manually_adjusted_area = true" 
                    class="button is-small ml-2">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                  </button>
                  <template v-else>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="adjustFabrication(itemFabrication.id, 
                        { area: itemFabrication.area, manually_adjusted_area: true })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fa fa-save"></i>
                      </span>
                    </button>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="itemFabrication.manually_adjusted_area = false; 
                        adjustFabrication(itemFabrication.id, { manually_adjusted_area: false })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fas fa-undo"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
              <td>
                {{ itemFabrication.raw_area | commasep(0) }}
              </td>
              <td class="border--after">
                {{ itemFabrication.fabrication.unit.unit_display_name }}
              </td>
              <td>
                {{ itemFabrication.unit_price | currency(2) }}
              </td>
              <td>
                {{ itemFabrication.price_bef_adj | currency(1) }}
              </td>
              <td>
                <div class="flex flex--row">
                  <v-text-field 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Unit Price Adjustment"
                    v-model="itemFabrication.unit_price_adj"
                    @keyup.enter.native="adjustFabrication(itemFabrication.id, 
                      { unit_price_adj: itemFabrication.unit_price_adj })"/>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustFabrication(itemFabrication.id, 
                      { unit_price_adj: itemFabrication.unit_price_adj })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </td>
              <td>
                {{ (+itemFabrication.unit_price + +itemFabrication.unit_price_adj) | currency(2) }}
              </td>
              <td>
                {{ itemFabrication.price | currency(1) }}
              </td>
            </tr>

            <tr 
              class="v-centered"
              v-for="(itemInstallation, installationIndex) in 
                item.installations.filter(d => 
                ![5,7].includes(d.installation.installation_type_id))"
              :key="`installation-material-${itemInstallation.id}`">
              <td
                class="border--after v-top"
                v-if="installationIndex == 0" 
                :rowspan="item.installations.length">
                Install
              </td>
              <td
                class="border--after v-top"
                v-if="installationIndex == 0" 
                :rowspan="item.installations.filter(d => 
                  ![5,7].includes(d.installation.installation_type_id)).length">
                Material
              </td>
              <td>
                {{ itemInstallation.installation.installation_type.installation_type_name }}
              </td>
              <td class="border--after">
                {{ itemInstallation.installation.installation_name }}
              </td>
              <td>
                <div class="flex flex--row flex--align-center">
                  <v-text-field
                    v-if="itemInstallation.manually_adjusted_area == true" 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Area"
                    v-model="itemInstallation.area"
                    @keyup.enter.native="adjustInstallation(itemInstallation.id, 
                      { area: itemInstallation.area, manually_adjusted_area: true })"/>
                  <div v-else>
                    {{ itemInstallation.area | commasep(0) }}
                  </div>
                  <button
                    :key="itemInstallation.manually_adjusted_area"
                    v-if="itemInstallation.manually_adjusted_area == false"
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="itemInstallation.manually_adjusted_area = true" 
                    class="button is-small ml-2">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                  </button>
                  <template v-else>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="adjustInstallation(itemInstallation.id, 
                        { area: itemInstallation.area, manually_adjusted_area: true })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fa fa-save"></i>
                      </span>
                    </button>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="itemInstallation.manually_adjusted_area = false; 
                        adjustInstallation(itemInstallation.id, { manually_adjusted_area: false })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fas fa-undo"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
              <td>
                {{ itemInstallation.raw_area | commasep(0) }}
              </td>
              <td class="border--after">
                {{ itemInstallation.installation.unit.unit_display_name }}
              </td>
              <td>
                {{ itemInstallation.unit_price | currency(2) }}
              </td>
              <td>
                {{ itemInstallation.price_bef_adj | currency(1) }}
              </td>
              <td>
                <div class="flex flex--row">
                  <v-text-field 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Unit Price Adjustment"
                    v-model="itemInstallation.unit_price_adj"
                    @keyup.enter.native="adjustInstallation(itemInstallation.id, 
                      { unit_price_adj: itemInstallation.unit_price_adj })"/>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustInstallation(itemInstallation.id, 
                      { unit_price_adj: itemInstallation.unit_price_adj })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </td>
              <td>
                {{ (+itemInstallation.unit_price + +itemInstallation.unit_price_adj) | currency(2) }}
              </td>
              <td>
                {{ itemInstallation.price | currency(1) }}
              </td>
            </tr>

            <tr 
              class="v-centered"
              v-for="(itemInstallation, installationIndex) in 
                item.installations.filter(d => 
                [5,7].includes(d.installation.installation_type_id))"
              :key="`installation-labour-${itemInstallation.id}`">
              <td
                class="border--after v-top"
                v-if="item.installations.filter(d => 
                ![5,7].includes(d.installation.installation_type_id)).length == 0" 
                :rowspan="item.installations.length">
                Install
              </td>
              <td
                class="border--after v-top"
                v-if="installationIndex == 0" 
                :rowspan="item.installations.filter(d => 
                  [5,7].includes(d.installation.installation_type_id)).length">
                Labour
              </td>
              <td>
                {{ itemInstallation.installation.installation_type.installation_type_name }}
              </td>
              <td class="border--after">
                {{ itemInstallation.installation.installation_name }}
              </td>
              <td>
                <div class="flex flex--row flex--align-center">
                  <v-text-field
                    v-if="itemInstallation.manually_adjusted_area == true" 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Area"
                    v-model="itemInstallation.area"
                    @keyup.enter.native="adjustInstallation(itemInstallation.id, 
                      { area: itemInstallation.area, manually_adjusted_area: true })"/>
                  <div v-else>
                    {{ itemInstallation.area | commasep(0) }}
                  </div>
                  <button
                    :key="itemInstallation.manually_adjusted_area"
                    v-if="itemInstallation.manually_adjusted_area == false"
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="itemInstallation.manually_adjusted_area = true" 
                    class="button is-small ml-2">
                    <span class="icon">
                      <i class="fa fa-edit"></i>
                    </span>
                  </button>
                  <template v-else>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="adjustInstallation(itemInstallation.id, 
                        { area: itemInstallation.area, manually_adjusted_area: true })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fa fa-save"></i>
                      </span>
                    </button>
                    <button
                      :disabled="savingSummary || !editable"
                      :class="{ 'is-loading': savingSummary }"
                      @click="itemInstallation.manually_adjusted_area = false; 
                        adjustInstallation(itemInstallation.id, { manually_adjusted_area: false })" 
                      class="button is-small ml-1">
                      <span class="icon">
                        <i class="fas fa-undo"></i>
                      </span>
                    </button>
                  </template>
                </div>
              </td>
              <td>
                {{ itemInstallation.raw_area | commasep(0) }}
              </td>
              <td class="border--after">
                {{ itemInstallation.installation.unit.unit_display_name }}
              </td>
              <td>
                {{ itemInstallation.unit_price | currency(2) }}
              </td>
              <td>
                {{ itemInstallation.price_bef_adj | currency(1) }}
              </td>
              <td>
                <div class="flex flex--row">
                  <v-text-field 
                    is-small
                    required
                    :disabled="!editable"
                    type="number"
                    placeholder="Unit Price Adjustment"
                    v-model="itemInstallation.unit_price_adj"
                    @keyup.enter.native="adjustInstallation(itemInstallation.id, 
                      { unit_price_adj: itemInstallation.unit_price_adj })"/>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustInstallation(itemInstallation.id, 
                      { unit_price_adj: itemInstallation.unit_price_adj })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                </div>
              </td>
              <td>
                {{ (+itemInstallation.unit_price + +itemInstallation.unit_price_adj) | currency(2) }}
              </td>
              <td>
                {{ itemInstallation.price | currency(1) }}
              </td>
            </tr>

          </tbody>

          <tfoot>
            <tr>
              <td 
                colspan="8" 
                class="has-text-weight-bold border--after">
                Total
              </td>
              <td>
                {{ item.total_price_bef_adj / item.total_area_sqft | currency(2) }}
              </td>
              <td>
                {{ item.total_price_bef_adj | currency(1) }}
              </td>
              <td>
                {{ (item.total_price - item.total_price_bef_adj) / item.total_area_sqft | currency(2) }}
              </td>
              <td>
                {{ (item.total_price / item.total_area_sqft) | currency(2) }}
              </td>
              <td>
                {{ item.total_price | currency(1) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </template>

      <table
        v-if="estimation.services.length > 0"
        class="table is-fullwidth has-smallest-font">
        <thead>
          <tr>
            <th 
              v-for="(header,i) in detailedSummaryHeaders"
              :colspan="header.colspan"
              :class="header.class"
              :key="i">
              {{ header.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr 
            class="v-centered"
            v-for="(estimationService, serviceIndex) in estimation.services"
            :key="`service-${estimationService.id}`">
            <td
              class="border--after v-top"
              v-if="serviceIndex == 0" 
              :rowspan="estimation.services.length"
              colspan="3">
              Service
            </td>
            <td
              colspan="2" 
              class="border--after">
              {{ estimationService.service.service_name }}
            </td>
            <td>
              <div class="flex flex--row flex--align-center">
                <v-text-field
                  v-if="estimationService.manually_adjusted_area == true" 
                  is-small
                  required
                  :disabled="!editable"
                  type="number"
                  placeholder="Area"
                  v-model="estimationService.area"
                  @keyup.enter.native="adjustService(estimationService.id, 
                    { area: estimationService.area, manually_adjusted_area: true })"/>
                <div v-else>
                  {{ estimationService.area | commasep(0) }}
                </div>
                <button
                  :key="estimationService.manually_adjusted_area"
                  v-if="estimationService.manually_adjusted_area == false"
                  :disabled="savingSummary || !editable"
                  :class="{ 'is-loading': savingSummary }"
                  @click="estimationService.manually_adjusted_area = true" 
                  class="button is-small ml-2">
                  <span class="icon">
                    <i class="fa fa-edit"></i>
                  </span>
                </button>
                <template v-else>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="adjustService(estimationService.id, 
                      { area: estimationService.area, manually_adjusted_area: true })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fa fa-save"></i>
                    </span>
                  </button>
                  <button
                    :disabled="savingSummary || !editable"
                    :class="{ 'is-loading': savingSummary }"
                    @click="estimationService.manually_adjusted_area = false; 
                      adjustService(estimationService.id, { manually_adjusted_area: false })" 
                    class="button is-small ml-1">
                    <span class="icon">
                      <i class="fas fa-undo"></i>
                    </span>
                  </button>
                </template>
              </div>
            </td>
            <td>
              {{ estimationService.raw_area | commasep(0) }}
            </td>
            <td class="border--after">
              {{ estimationService.service.unit.unit_display_name }}
            </td>
            <td>
              {{ estimationService.unit_price | currency(2) }}
            </td>
            <td>
              {{ estimationService.price_bef_adj | currency(1) }}
            </td>
            <td>
              <div class="flex flex--row">
                <v-text-field 
                  is-small
                  required
                  :disabled="!editable"
                  type="number"
                  placeholder="Unit Price Adjustment"
                  v-model="estimationService.unit_price_adj"
                  @keyup.enter.native="adjustService(estimationService.id, 
                    { unit_price_adj: estimationService.unit_price_adj })"/>
                <button
                  :disabled="savingSummary || !editable"
                  :class="{ 'is-loading': savingSummary }"
                  @click="adjustService(estimationService.id, 
                    { unit_price_adj: estimationService.unit_price_adj })" 
                  class="button is-small ml-1">
                  <span class="icon">
                    <i class="fa fa-save"></i>
                  </span>
                </button>
              </div>
            </td>
            <td>
              {{ (+estimationService.unit_price + +estimationService.unit_price_adj) | currency(2) }}
            </td>
            <td>
              {{ estimationService.price | currency(1) }}
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td 
              colspan="8" 
              class="has-text-weight-bold border--after">
              Total
            </td>
            <td>
              {{ estimation.total_service_price_bef_adj / 
                estimation.total_area_sqft | currency(2) }}
            </td>
            <td>
              {{ estimation.total_service_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ (estimation.total_service_price - estimation.total_service_price_bef_adj) / 
                estimation.total_area_sqft | currency(2) }}
            </td>
            <td>
              {{ (estimation.total_service_price / estimation.total_area_sqft) | currency(2) }}
            </td>
            <td>
              {{ estimation.total_service_price | currency(1) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div
      v-if="active == 'summary' && !firstLoad" 
      class="estimation__details pb-3">

      <article 
        v-if="!loading && details.length == 0" 
        class="message is-info is-fullwidth mt-3">
        <div class="message-body">
          There are no details for this estimation.
        </div>
      </article>

      <table class="table is-fullwidth is-hoverable">
        <thead>
          <tr>
            <th 
              v-for="(header,i) in summaryHeaders"
              :class="header.class"
              :key="i">
              {{ header.label }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr 
            v-for="item in estimation.items"
            :key="item.id">
            <td class="border--after">
              {{ item.architecture_name }}
            </td>
            <td class="border--after">
              {{ item.total_area_sqft | commasep(0) }}
            </td>
            <td>
              {{ (+item.total_material_price_bef_adj + 
                +item.total_processing_price_bef_adj + 
                +item.total_fabrication_price_bef_adj) | currency(1) }}
            </td>
            <td class="border--after">
              {{ item.total_installation_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ item.total_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ item.total_adj | currency(1) }}
            </td>
            <td>
              {{ item.total_price | currency(1) }}
            </td>
          </tr>

          <tr v-if="estimation.services.length > 0">
            <th
              class="has-text-centered border--after"
              :colspan="summaryHeaders.length - 3">
              Services
            </th>
            <td>
              {{ estimation.total_service_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ estimation.total_service_price_adj | currency(1) }}
            </td>
            <td>
              {{ estimation.total_service_price | currency(1) }}
            </td>
          </tr>
        </tbody>
        
        <tfoot>
          <tr>
            <td class="has-text-weight-bold border--after">
              Total
            </td>
            <td class="border--after">
              {{ estimation.total_area_sqft | commasep(0) }}
            </td>
            <td>
              {{ (+estimation.total_material_price_bef_adj + 
                +estimation.total_processing_price_bef_adj + 
                +estimation.total_fabrication_price_bef_adj) | currency(1) }}
            </td>
            <td class="border--after">
              {{ estimation.total_installation_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ estimation.total_price_bef_adj | currency(1) }}
            </td>
            <td>
              {{ estimation.total_adj | currency(1) }}
            </td>
            <td>
              {{ estimation.total_price_pre_tax | currency(1) }}
            </td>
          </tr>
          <tr>
            <td colspan="6" class="has-text-weight-bold">Tax</td>
            <td>
              {{ estimation.total_price_post_tax - estimation.total_price_pre_tax 
                | currency(1) }}
            </td>
          </tr>
          <tr>
            <td colspan="6" class="has-text-weight-bold">Total After Tax</td>
            <td>{{ estimation.total_price_post_tax | currency(1) }}</td>
          </tr>
        </tfoot>
      </table>

    </div>

    <div 
      v-if="active == 'proposal_preview'"
      class="container py-2">
      <proposal-preview-cladify 
        :estimation="estimation"/>
    </div>

    <bulk-adjust-price-modal
      v-if="!firstLoad"
      v-model="bulkAdjustingPrice"
    />

    <load-from-draft-modal 
      v-if="draft && draft.data" 
      v-model="loadFromDraftModal"
      @confirm="loadEstimationFromDraft"
    />
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import ProposalPreviewCladify from '@/components/proposalmaker/ProposalPreviewCladify'
import DrawingHighlights from '@/components/estimations/DrawingHighlights'
import BulkAdjustPriceModal from '@/components/estimations/BulkAdjustPriceModal'
import LoadFromDraftModal from '@/components/estimations/LoadFromDraftModal'
import { parseJSON, format } from 'date-fns'
import { mapState, mapActions, mapMutations } from 'vuex'
import helpers from '@/helpers'
import jexcel from 'jexcel'
import 'jexcel/dist/jexcel.css'

export default {
  props: {
    editable: {
      type: Boolean
    },
    adjustPrice: {
      type: Function
    },
    updateInsulationParam: {
      type: Function
    },
    can: {
      type: Object
    },
    setSaving: {
      type: Function
    },
    active: {
      type: String
    }
  },
  components: {
    VTextField, ProposalPreviewCladify, DrawingHighlights,
    BulkAdjustPriceModal, LoadFromDraftModal
  },
  data() {
    return {
      loadFromDraftModal: false,
      draftInterval: null,
      loadedFromDraft: false,
      bulkAdjustingPrice: false,
      fields: [
        'id', 'elevation_id', 'estimation_item_id', 'details', 'length_in', 
        'width_in', 'panels', 'area', 'linear_type', 'linear_in'
      ],
      modifiedDetails: [],
      originalIds: [],
      insulationHeaders: [
        'Item Architectural Name', 'Insulation Selection'
      ],
      detailedSummaryHeaders: [
        { label: 'Item', colspan: 3, class: 'border--after' }, 
        { label: 'Description', colspan: 2, class: 'border--after' },
        { label: 'Area' }, { label: 'Raw Area' }, 
        { label: 'Unit', class: 'border--after' }, 
        { label: 'Unit Price' }, { label: 'Standard Cost' }, 
        { label: 'Unit Price Adjustment' }, 
        { label: 'Adjusted Unit Price' }, 
        { label: 'Adjusted Price' }
      ],
      summaryHeaders: [
        { label: 'Item', class: 'border--after' }, 
        { label: 'Total Area (sqft)', class: 'border--after' },
        { label: 'Supply' },
        { label: 'Install', class: 'border--after' },
        { label: 'Total Value' },
        { label: 'Price Adjustment' }, 
        { label: 'Customer Price' }
      ],
      detailWidth: '500',
      parseDate: (date) => { return date ? parseJSON(date) : date },
      formatDate: (date) => format(date, "yyyy-MM-dd HH:mm:ss"),
    }
  },
  computed: {
    ...mapState("cladifyEstimation", [
      "estimation", "loading", "firstLoad", "draft", "draftSavedAt"
    ]),
    ...mapState("elevations", {
      elevations: "elevations", 
      elevationsLoading: "loading"
    }),
    ...mapState("installations", ["installations"]),
    hasInsulation({ estimation }) {
      return estimation.items.map(d => d.has_insulation == true).length > 0;
    },
    details({ estimation, fields }) {
      let res = estimation.items.map(d => d.elevations).flat(1);
      let empty = new Array(fields.length);

      if (res.length > 0) {
        res = res.sort((a,b) => {
          if (a.order == b.order) {
            return +a.id - +b.id
          } else {
            return +a.order - +b.order
          }
        });
        res = res.map(d => {
          return fields.map(field => d[field]);
        });

        return res.length > 0 ? res : [empty];
      } else {
        return [empty];
      }
    },
    totCols() {
      return this.fields.length;
    },
    options({ detailWidth, totCols, elevations, estimation }) {
      let cellWidth = detailWidth / (totCols - 1);

      return {
        data: this.details,
        allowInsertColumn: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        columnResize: false,
        columns: [
          { type: 'numeric', width: `${cellWidth}px`, title: 'Id' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Elevation', 
            source: elevations.map(d => {
              d.name = d.elevation_name; 
              return d
            })
          },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Item', 
            source: estimation.items.map(d => {
              d.name = d.architecture_name;
              return d
            }),
            autocomplete: true
          },
          { type: 'text', width: `${cellWidth}px`, title: 'Details' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'L"' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'W"' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Quantity' },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Area (sqft)' },
          { 
            type: 'dropdown', 
            width: `${cellWidth}px`, 
            title: 'Linear Type', 
            source: ['opening', 'pre-assemble'] 
          },
          { type: 'numeric', width: `${cellWidth}px`, title: 'Linear Inch' }
        ],
        oninsertrow: () => { this.spreadsheet.hideColumn(0) },
        ondeleterow: (instance) => {
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
        },
        onchange: (instance, cell, x, y, v) => { 
          if (this.modifiedDetails.length == 0) {
            this.triggerAutoSave();
          }
          this.fieldChanged(instance, cell, x, y, v); 
        },
        updateTable: (el, cell, x) => {
          const sqftInd = this.fields.indexOf('area');

          if (!this.editable || !this.can.markAsReadyForPricing || this.estimation.is_ready_for_pricing) {
            cell.classList.add('readonly');
          } else if (
            sqftInd == +x
          ) {
            cell.classList.add('readonly'); 
          }
        }
      }
    },
    exceptions({ fields }) {
      return [fields.indexOf('elevation_id'), fields.indexOf('estimation_item_id')];
    },
    numberFields({ fields }) {
      return [fields.indexOf('length_in'), fields.indexOf('width_in'), fields.indexOf('panels')];
    },
    hasUnsavedDraft({ draft, lastManualSave }) {
      return this.parseDate(draft.created_on) > lastManualSave
    }
  },
  beforeDestroy() {
    clearInterval(this.draftInterval);
  },
  methods: {
    ...mapActions("cladifyEstimation", [
      "getEstimation", "updateEstimation", "addItemElevation", "updateItemElevation", 
      "deleteItemElevation", "updateElevationOrders", "addItemInstallation", 
      "updateItemInstallation", "updateEstimator", "updateItemMaterial", 
      "updateItemProcessing", "updateItemFabrication", "updateService",
      "saveDraft", "getDraft"
    ]),
    ...mapActions("elevations", ["getElevations"]),
    ...mapMutations("app", ["setNotification"]),
    ...mapActions("installations", ["getInstallations"]),
    initializeEstimation() {
      if (this.installations.length == 0) {
        this.getInstallations();
      }
      this.fetchDraft();
      this.setOriginalIds();
      this.checkElevations();
    },
    setOriginalIds() {
      this.originalIds = [...this.estimation.items.map(d => d.elevations).flat(1).map(d => d.id)];
    },
    checkElevations() {
      if (this.elevations.length == 0) {
        this.getElevations().then(() => {
          this.initializeSpreadsheet();
        })
      } else {
        this.initializeSpreadsheet();
      }
    },
    updateInsulation(installation, v) {
      this.updateItemInstallation({
        estimationId: this.estimation.id,
        itemInstallationId: installation.id,
        data: { installation_id: v }
      });
    },
    addInsulation(item, v) {
      this.addItemInstallation({
        estimation_item_id: item.id,
        installation_id: v
      }).then(() => {
        this.getEstimation(this.$route.params.estimationId).then(() => {
          this.savingSummary = false;
        });
      });
    },
    adjustMaterial(itemMaterialId, payload) {
      this.savingSummary = true;

      this.updateItemMaterial({
        estimationId: this.estimation.id,
        itemMaterialId: itemMaterialId,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    adjustProcessing(itemProcessingId, payload) {
      this.savingSummary = true;

      this.updateItemProcessing({
        estimationId: this.estimation.id,
        itemProcessingId: itemProcessingId,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    adjustFabrication(itemFabricationId, payload) {
      this.savingSummary = true;

      this.updateItemFabrication({
        estimationId: this.estimation.id,
        itemFabricationId: itemFabricationId,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    adjustInstallation(itemInstallationId, payload) {
      this.savingSummary = true;

      this.updateItemInstallation({
        estimationId: this.estimation.id,
        itemInstallationId: itemInstallationId,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    adjustService(serviceId, payload) {
      this.savingSummary = true;

      this.updateService({
        estimationId: this.estimation.id,
        serviceId: serviceId,
        data: payload
      }).then(
        () => this.savingSummary = false
      ).catch(() => this.savingSummary = false);
    },
    initializeSpreadsheet() {
      this.setWidth();

      // Initialize spreadsheet
      let el = document.getElementById("estimation-data");
      let spreadsheet = jexcel(
        el, this.options
      );
      Object.assign(this, { spreadsheet });
      spreadsheet.hideColumn(0);
    },
    setWidth() {
      let el = document.querySelector('#estimation');
      this.detailWidth = el.clientWidth - parseFloat(helpers.getStyle(
        el, 'padding-left'
      ).replace("px", "")) - parseFloat(helpers.getStyle(
        el, 'padding-right'
      ).replace("px", "")) - 43.6 - 36;
    },
    fieldChanged(instance, cell, x, y, v) {
      const recalc = this.numberFields.includes(+x);
      /////////////////////////////
      //  Record modification
      ////////////////////////////
      const { fields } = this;
      let row = this.spreadsheet.getRowData(y);
      let idInd = fields.indexOf('id');
      if (idInd != -1) {
        let ind = this.modifiedDetails.indexOf(row[idInd]);
        if (ind == -1) {
          this.modifiedDetails.push(row[idInd]);
        }
      }

      if (recalc && v.toString().indexOf(',') != -1) {
        this.spreadsheet.setValueFromCoords(
          x,y,v.replaceAll(',', ''),true
        )
      } else {
        const widthInd = fields.indexOf('width_in'),
            lengthInd = fields.indexOf('length_in'),
            qtyInd = fields.indexOf('panels'),
            sqftInd = fields.indexOf('area');

        const length = this.spreadsheet.getCellFromCoords(lengthInd, y).innerHTML,
              width = this.spreadsheet.getCellFromCoords(widthInd, y).innerHTML,
              quant = this.spreadsheet.getCellFromCoords(qtyInd, y).innerHTML;

        // SQFT Calculation
        if (recalc) {
          let sqft = +length * +width * +quant / 144

          this.spreadsheet.setValueFromCoords(
            sqftInd,y,this.$options.filters.round(sqft, 2),true
          )
        }
      }
    },
    getData(validate=true, obj=true) {
      const {fields, exceptions} = this;
      let dataRaw = this.spreadsheet.getData();
      let data = this.spreadsheet.records.map(
        x=>x.map(y=>y.innerHTML)
      ).map((d, rowIndex) => {
        if (obj) {
          return Object.fromEntries(
            fields.map((_, i) => {
              if (exceptions.includes(i)) {
                let v = dataRaw[rowIndex][i];
                v = v == "" ? null : v.toString();
                return [fields[i], v];
              } else {
                let v = d[i] == "" ? null : d[i].toString(); 
                return [fields[i], v];
              }
            })
          )
        } else {
          return fields.map((_, i) => {
            if (exceptions.includes(i)) {
              let v = dataRaw[rowIndex][i];
              v = v == "" ? null : v.toString();
              return v;
            } else {
              let v = d[i] == "" ? null : d[i].toString(); 
              return v;
            }
          })
        }
      })

      if (validate) {
        return data.filter(d => d.estimation_item_id != null && d.panels != null);
      } else {
        return data
      }
    },
    triggerAutoSave() {
      // Auto-save every 30 seconds
      let data = this.getData(false, false);

      this.saveDraft({
        estimationId: this.$route.params.estimationId,
        data: data
      });

      this.draftInterval = setInterval(() => {
        this.updateDraft();
      }, 30000);
    },
    updateDraft() {
      let data = this.getData(false, false);

      this.saveDraft({
        estimationId: this.$route.params.estimationId,
        data: data
      });
    },
    fetchDraft() {
      if (!this.estimation.is_ready_for_pricing) {
        this.getDraft(this.estimation.id).then(() => {
          if (this.draft.data) {
            let data = this.draft.data;

            let diff = this.compareArr(data, this.details);

            if (this.parseDate(this.draft.created_on) > this.lastManualSave && 
              diff.length > 0) {
              this.loadFromDraftModal = true;
            }
          }
        })
      }
    },
    loadEstimationFromDraft() {
      let data = this.draft.data;

      this.refreshTables(data);
      this.modifiedDetails = this.compareArr(data, this.details).filter(d => typeof d != 'string');
    },
    reset() {
      this.setOriginalIds();
      this.modifiedDetails = [];
    },
    refreshTables(data) {
      data = data ? data : this.details;
      this.spreadsheet.setData(data);
      this.spreadsheet.refresh();
      this.spreadsheet.hideColumn(0);
    },
    saveFullEstimation() {
      //////////////////////////////////
      // Clean up data
      //////////////////////////////////
      let data = this.getData();

      //////////////////////////////////
      // Add the order
      //////////////////////////////////
      data.forEach((row, i) => {
        row.order = i;
      });

      let newOrder = data.map(
        d => { return { id: d.id, order: d.order } }
      ).filter(d => d.id != null);

      let promises = [];

      //////////////////////////////////
      // Delete
      //////////////////////////////////
      let newIds = [...data.map(d => +d.id)];
      let deleteIds = this.originalIds.filter(id => !newIds.includes(+id));
      deleteIds.forEach(deleteId => {
        promises.push(this.deleteItemElevation(deleteId));
      })

      //////////////////////////////////
      // Add
      //////////////////////////////////
      let newDetails = data.filter(d => !d.id).map(d => {
        delete d.id;
        return d
      })

      newDetails.forEach(detail => {
        promises.push(this.addItemElevation(detail));
      })
      
      //////////////////////////////////
      // Modify
      //////////////////////////////////
      let modifyIds = this.modifiedDetails.filter(d => !deleteIds.includes(d));
      let updateDetails = data.filter(d => !!d.id && modifyIds.includes(+d.id));

      updateDetails.forEach(detail => {
        promises.push(
          this.updateItemElevation({
            itemElevationId: detail.id,
            data: detail
          })
        )
      });

      return new Promise((resolve, reject) => {
        // Notify success
        Promise.all(promises).then(resp => {
          this.updateElevationOrders(newOrder).then(() => {
            this.setNotification({
              status: true,
              type: 'success',
              message: 'Estimation successfully updated'
            });
            this.getEstimation(this.$route.params.estimationId).then(() => {
              this.reset();
              this.refreshTables();
              this.setSaving(false);
              resolve(resp);
            })
          }).catch(err => {
            this.getEstimation(this.$route.params.estimationId).then(() => {
              this.reset();
              this.refreshTables();
              this.setSaving(false);
              reject(err);
            });
          }); 
        }).catch(err => {
          this.getEstimation(this.$route.params.estimationId).then(() => {
            this.reset();
            this.refreshTables();
            this.setSaving(false);
            reject(err);
          });
        })
      });
    }
  }
}
</script>


<style lang="scss" scoped>
@import '@/styles/flexbox.scss';
@import '@/styles/table.scss';

.estimation {
  &__details {
    width: 100%;
    min-height: 100vh;
    overflow-x: auto;
    overflow-y: visible;
    padding: 0em 1em;
  }
}
</style>